// src/pages/OltEquipo.jsx — configurar la OLT
//
// Todo lo de esta pantalla ESCRIBE en un equipo que sostiene a mil
// trescientos clientes. Dos consecuencias de forma:
//
//   · Los comandos que se mandan son los de CONVENCIONES-OLT.md, y se
//     enseñan después de aplicarlos para que quede claro qué se hizo.
//
//   · Cuando el equipo contesta algo que el parser no entiende, se enseña
//     el TEXTO CRUDO. Una tabla vacía que en realidad significa "no supe
//     leer esto" manda a buscar un problema que no existe.

import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Boton, Iconos, Tarjeta } from "../componentes/base";

/**
 * El estado de un puerto de subida, de `display board 0/<ranura>`:
 *
 *   · con enlace       — `Link: online`
 *   · módulo sin enlace— hay óptica metida (`Optic Status: normal`) pero
 *                        no levanta. Es el caso que hay que poder ver.
 *   · sin módulo       — `absence`, la jaula está vacía y eso es normal
 *                        en los puertos de repuesto.
 *
 * Se nombran por lo que son y no como una alarma: un puerto de repuesto
 * con un módulo olvidado dentro no es una avería.
 */
function estadoSubida(u) {
  if (u.enlace === "online") return { clave: "arriba", texto: "con enlace" };
  if (u.optico === "absence") return { clave: "libre", texto: "sin módulo" };
  return { clave: "sinenlace", texto: "módulo sin enlace" };
}

/* Los MISMOS comandos que arma el backend, repetidos aquí para poder
 * ENSEÑARLOS antes de mandarlos. La autoridad sigue siendo el servidor:
 * esto es la vista previa, no la fuente. Si alguna vez dejan de coincidir,
 * el que manda es Python. */

/** `0/9/3` -> `0/9 3`, que es como lo escribe `port vlan`. */
const puertoCorto = (fsp) => {
  const [frame, slot, puerto] = String(fsp).split("/");
  return `${frame}/${slot} ${puerto}`;
};

const comandosCrearVlan = (vlan, uplinks) => [
  `vlan ${vlan} smart`,
  ...uplinks.map((u) => `port vlan ${vlan} ${puertoCorto(u)}`),
];

// Primero se sueltan los puertos y después se borra la VLAN: con un
// puerto todavía amarrado el `undo vlan` no pasa.
const comandosBorrarVlan = (vlan, puertos) => [
  ...puertos.map((p) => `undo port vlan ${vlan} ${puertoCorto(p)}`),
  `undo vlan ${vlan}`,
];

const comandoPuerto = (vlan, fsp, quitar) =>
  `${quitar ? "undo " : ""}port vlan ${vlan} ${puertoCorto(fsp)}`;

/** Cómo se pinta un PON en la vista de hardware: aquí manda el MÓDULO
 *  ÓPTICO del puerto, no los clientes que cuelguen de él. */
function estadoGpon(p) {
  return p.optico === "online"
    ? { clave: "arriba", texto: "módulo en línea" }
    : { clave: "caido", texto: `módulo ${p.optico}` };
}

/**
 * Las placas de CONTROL, dibujadas como se ven de frente, con toda la
 * información de cada puerto de subida dentro de su propia celda: el
 * enlace, la velocidad y las VLANs que lleva.
 *
 * Van juntas y no en una tabla aparte porque son la misma pregunta: de
 * estos ocho puertos, cuál está arriba y qué saca. Las GPON no están aquí
 * —viven en el Panel, que es donde se mira el servicio.
 */
function ChasisControl({ placas, vlansPorPuerto }) {
  if (!placas.length) {
    return <p className="sw-tenue">No se encontraron tarjetas de control.</p>;
  }

  return (
    <div className="olt-chasis">
      {placas.map((pl) => (
        <div key={pl.slot} className="olt-ranura olt-ranura--control">
          <span className="olt-ranura__id">0/{pl.slot}</span>

          <div className="olt-subidas">
            {(pl.puertos || []).map((p) => {
              const est = estadoSubida(p);
              const fsp = `0/${pl.slot}/${p.puerto}`;
              const vlans = vlansPorPuerto.get(fsp) || [];
              return (
                <div key={p.puerto} className={`olt-subida olt-subida--${est.clave}`}>
                  <b>{fsp}</b>
                  <span className="olt-subida__estado">{est.texto}</span>
                  <span className="olt-subida__vel">
                    {[p.tipo, p.velocidadMbps ? `${p.velocidadMbps / 1000} Gbps` : null]
                      .filter(Boolean).join(" · ")}
                  </span>
                  <div className="olt-subida__vlans">
                    {vlans.length
                      ? vlans.map((v) => <em key={v} className="sw-t">{v}</em>)
                      : <span className="sw-tenue">sin VLANs</span>}
                  </div>
                </div>
              );
            })}
            {!pl.puertos?.length && (
              <span className="olt-vacio">sin puertos que dibujar</span>
            )}
          </div>

          <div className="olt-ranura__tarjeta">
            <b>{pl.modelo}</b>
            <em className={/active/i.test(pl.estado || "") ? "olt-activa" : "sw-tenue"}>
              {pl.estado}
            </em>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Salida del equipo que no se pudo interpretar. Se enseña tal cual. */
function Crudo({ titulo, texto }) {
  if (!texto || !texto.trim()) return null;
  return (
    <details className="olt-crudo">
      <summary>{titulo}</summary>
      <pre>{texto}</pre>
    </details>
  );
}

export default function OltEquipo({ oltId, host, datos, recargar, recargando }) {
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const [vlanNueva, setVlanNueva] = useState("");
  const [uplinksNueva, setUplinksNueva] = useState([]);
  const [gestion, setGestion] = useState({ ip: "", mascara: "255.255.255.0", tocada: false });
  // Nada se manda a la OLT sin enseñarlo antes. `plan` es lo que se va a
  // mandar —título, comandos y qué hacer al confirmar— y el modal es uno
  // solo para todas las acciones.
  const [plan, setPlan] = useState(null);

  // La máscara que tiene el equipo entra sola en el formulario: casi
  // siempre se cambia la IP conservándola, y volver a escribirla es una
  // oportunidad más de equivocarse. Deja de rellenarse al tocarla.
  useEffect(() => {
    const m = datos?.gestion?.mascara;
    if (m) setGestion((g) => (g.tocada ? g : { ...g, mascara: m }));
  }, [datos]);

  // El resultado no se queda para siempre en pantalla: a los diez segundos
  // ya no informa, estorba.
  useEffect(() => {
    if (!mensaje) return undefined;
    const t = setTimeout(() => setMensaje(null), 10000);
    return () => clearTimeout(t);
  }, [mensaje]);

  /** Manda algo al equipo y vuelve a leer: lo que se ve es lo que quedó. */
  const mandar = async (url, opciones, exito) => {
    setOcupado(true);
    setError(null);
    setMensaje(null);
    try {
      const res = await authFetch(url, opciones);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "El equipo rechazó el cambio.");
      setMensaje(exito);
      await recargar();
      return data;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setOcupado(false);
    }
  };

  const json = (cuerpo) => ({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(cuerpo),
  });

  const darDeAlta = (placa) => setPlan({
    titulo: `Dar de alta la tarjeta 0/${placa.slot}`,
    comandos: [`board add 0/${placa.slot} ${placa.modelo}`],
    correr: () => mandar(
      `/api/olt/${oltId}/placas`,
      json({ slot: placa.slot, modelo: placa.modelo }),
      `Tarjeta 0/${placa.slot} dada de alta.`,
    ),
  });

  const crearVlan = (subida) => {
    const n = parseInt(vlanNueva, 10);
    if (!n) { setError("Escribe el número de la VLAN."); return; }
    if (!uplinksNueva.length) {
      // Está en CONVENCIONES y cuesta una tarde encontrarlo: la VLAN sola
      // no saca tráfico de la OLT.
      setError("Elige al menos un puerto de subida. Una VLAN sin puerto de subida no saca tráfico de la OLT.");
      return;
    }
    setError(null);
    setPlan({
      titulo: `Crear la VLAN ${n}`,
      comandos: comandosCrearVlan(n, uplinksNueva),
      correr: () => mandar(
        `/api/olt/${oltId}/vlans`,
        json({ vlan: n, uplinks: uplinksNueva }),
        `VLAN ${n} creada y amarrada a ${uplinksNueva.join(", ")}.`,
      ).then((ok) => { if (ok) { setVlanNueva(""); setUplinksNueva([]); } }),
    });
  };

  const borrarVlan = (v) => setPlan({
    titulo: `Borrar la VLAN ${v.id}`,
    comandos: comandosBorrarVlan(v.id, v.puertos),
    aviso: "Los clientes que tengan service-ports en esa VLAN se quedan sin servicio.",
    peligro: true,
    correr: () => mandar(
      `/api/olt/${oltId}/vlans/${v.id}`,
      { method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ puertos: v.puertos }) },
      `VLAN ${v.id} borrada.`,
    ),
  });

  const cambiarPuerto = (vlan, uplink, quitar) => setPlan({
    titulo: quitar
      ? `Sacar ${uplink} de la VLAN ${vlan}`
      : `Meter ${uplink} a la VLAN ${vlan}`,
    comandos: [comandoPuerto(vlan, uplink, quitar)],
    correr: () => mandar(
      `/api/olt/${oltId}/vlans/${vlan}/puerto`,
      { ...json({ uplink, quitar }), method: "PUT" },
      quitar ? `${uplink} fuera de la VLAN ${vlan}.` : `${uplink} metido a la VLAN ${vlan}.`,
    ),
  });

  const cambiarIp = () => setPlan({
    titulo: "Cambiar la IP de gestión",
    comandos: ["config", "interface meth 0",
               `ip address ${gestion.ip} ${gestion.mascara}`, "quit"],
    aviso: `La sesión se corta en cuanto la interfaz cambie de IP —es lo esperado— y `
      + `la app pasa a entrar por ${gestion.ip}. Si el equipo no responde ahí, se arregla por consola.`,
    peligro: true,
    correr: () => mandar(
      `/api/olt/${oltId}/gestion`,
      { ...json({ ip: gestion.ip, mascara: gestion.mascara, confirmar: true }), method: "PUT" },
      // `mandar` ya vuelve a leer el equipo entero al terminar, y de ahí
      // sale la IP nueva: no hace falta avisar a nadie más.
      `La app ya apunta a ${gestion.ip}.`,
    ),
  });

  const aplicarPlan = async () => {
    const actual = plan;
    await actual.correr();
    setPlan(null);
  };


  const { placas, subida, vlans, crudo, gestion: meth, descuadre } = datos;
  const sinAlta = placas.filter((p) => !p.registrada);
  const control = placas.filter((p) => p.tipo === "CONTROL" || p.tipo === "SUBIDA");
  // Qué VLANs lleva cada puerto de subida, para meterlo en su celda.
  const vlansPorPuerto = new Map(subida.map((u) => [u.fsp, u.vlans]));
  // Los puertos de subida agrupados por tarjeta, para que el encabezado
  // de la matriz diga `0/8` una vez y debajo sus cuatro números.
  const tarjetasDeSubida = [...subida.reduce((mapa, u) => {
    const clave = u.fsp.split("/").slice(0, 2).join("/");
    if (!mapa.has(clave)) mapa.set(clave, []);
    mapa.get(clave).push(u);
    return mapa;
  }, new Map())];
  // Las que no sacan tráfico. La 1 es la de fábrica y nunca lleva
  // salida, así que contarla sería un aviso imposible de atender.
  const sinSalida = vlans.filter((v) => v.id !== 1 && v.puertos.length === 0);

  return (
    <>
      {error && (
        <p className="sw-aviso sw-aviso--bloqueo">
          <Iconos.error size={16} strokeWidth={2} />{error}
        </p>
      )}
      {mensaje && <p className="sw-aviso sw-aviso--exito">{mensaje}</p>}

      {/* ---------- las placas de control ---------- */}
      {/* La alta de una tarjeta va arriba y no dentro del dibujo: es una
          acción que aparece de higos a brevas —solo cuando entra hardware
          nuevo— y buscarla entre las ranuras sería peor que tenerla aquí. */}
      {sinAlta.length > 0 && (
        <Tarjeta titulo="Tarjetas sin dar de alta">
          <div className="sw-aviso sw-aviso--suave">
            <Iconos.duda size={16} strokeWidth={2} />
            <div>
              <p>
                <strong>
                  {sinAlta.length === 1
                    ? "La OLT ve una tarjeta que no tiene dada de alta."
                    : `La OLT ve ${sinAlta.length} tarjetas que no tiene dadas de alta.`}
                </strong>
              </p>
              <p>Mientras estén en <code>Auto_find</code> no admiten perfiles ni service-ports.</p>
            </div>
          </div>
          <div className="olt-altas">
            {sinAlta.map((pl) => (
              <div key={pl.slot} className="olt-alta">
                <span className="mono">0/{pl.slot}</span>
                <b>{pl.modelo}</b>
                <span className="sw-tenue">{pl.tipo}</span>
                <Boton
                  variante="secundario"
                  icono={Iconos.agregar}
                  onClick={() => darDeAlta(pl)}
                  disabled={ocupado}
                >
                  Dar de alta
                </Boton>
              </div>
            ))}
          </div>
        </Tarjeta>
      )}

      <Tarjeta
        titulo="Placas de control"
        acciones={
          <Boton variante="secundario" icono={Iconos.actualizar} onClick={recargar} cargando={recargando}>
            Actualizar
          </Boton>
        }
      >
        <ChasisControl placas={control} vlansPorPuerto={vlansPorPuerto} />

        <div className="sw-leyenda">
          <span><i className="olt-punto olt-pon--arriba" />con enlace</span>
          <span><i className="olt-punto olt-pon--sinenlace" />módulo puesto, sin enlace</span>
          <span><i className="olt-punto olt-pon--libre" />jaula vacía</span>
        </div>

        <p className="sw-pie-leyenda">
          «Activa» y «en espera» son de la tarjeta —cuál de las dos MPU
          manda— y es cosa distinta de que el puerto tenga enlace. Las
          tarjetas GPON y el estado de sus ONTs van en el Panel.
        </p>

        {control.length === 0 && <Crudo titulo="Salida de display board 0" texto={crudo.board} />}
      </Tarjeta>

      {/* ---------- VLANs ---------- */}
      <Tarjeta
        titulo="VLANs"
        acciones={
          <span className="olt-resumen">
            {vlans.length} {vlans.length === 1 ? "VLAN" : "VLANs"}
            {sinSalida.length > 0 && (
              <em className="olt-resumen--aviso">
                {sinSalida.length} sin salida
              </em>
            )}
          </span>
        }
      >
        {/* Las VLANs se leen de dos sitios distintos: `display vlan all`
            dice CUÁNTOS puertos de subida tiene cada una, y cada puerto
            dice qué VLANs lleva. Si las dos cuentas no coinciden, la
            matriz miente, y eso hay que decirlo en vez de pintarla. */}
        {descuadre?.vlans?.length > 0 && (
          <div className="sw-aviso sw-aviso--bloqueo">
            <Iconos.error size={16} strokeWidth={2} />
            <div>
              <p>
                <strong>
                  Las dos lecturas no coinciden en {descuadre.vlans.length === 1
                    ? `la VLAN ${descuadre.vlans[0]}`
                    : `las VLANs ${descuadre.vlans.join(", ")}`}.
                </strong>
              </p>
              <p>
                La matriz de abajo puede estar incompleta. La salida cruda
                del equipo está al final de la tarjeta.
              </p>
            </div>
          </div>
        )}

        {/* Matriz, como la del switch: los puertos arriba una sola vez —la
            tarjeta y debajo su número— y un renglón por VLAN. La VLAN nueva
            se arma en su propio renglón, con las mismas celdas, para que
            crear y editar se hagan igual. */}
        <div className="olt-matriz">
          <div className="olt-matriz__encabezado">
            <span className="olt-matriz__vlan">VLAN</span>
            <div className="olt-matriz__puertos">
              {tarjetasDeSubida.map(([tarjeta, puertos]) => (
                <div key={tarjeta} className="olt-matriz__grupo">
                  <span className="olt-matriz__tarjeta">{tarjeta}</span>
                  <div className="olt-matriz__fila">
                    {puertos.map((u) => (
                      <span key={u.fsp} className="olt-matriz__num">{u.puerto}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <span className="olt-matriz__acciones" />
          </div>

          {vlans.map((v) => {
            // La VLAN 1 es la de fábrica y nunca lleva salida: marcarla
            // como problema sería un aviso que no se puede atender.
            const huerfana = v.id !== 1 && v.puertos.length === 0;
            return (
              <div key={v.id} className="olt-matriz__renglon">
                <div className="olt-matriz__vlan">
                  <b>{v.id}</b>
                  <span>{v.descripcion || "—"}</span>
                  <em>
                    {v.tipo}
                    {v.estado === "disable" && (
                      <i className="olt-apagada">apagada</i>
                    )}
                    {huerfana && <i className="olt-huerfana">sin salida</i>}
                  </em>
                </div>

                <div className="olt-matriz__puertos">
                  {tarjetasDeSubida.map(([tarjeta, puertos]) => (
                    <div key={tarjeta} className="olt-matriz__grupo">
                      <div className="olt-matriz__fila">
                        {puertos.map((u) => {
                          const puesto = v.puertos.includes(u.fsp);
                          return (
                            <button
                              key={u.fsp}
                              type="button"
                              className={`olt-celda${puesto ? " olt-celda--puesta" : ""}`}
                              onClick={() => cambiarPuerto(v.id, u.fsp, puesto)}
                              disabled={ocupado}
                              title={puesto
                                ? `Sacar ${u.fsp} de la VLAN ${v.id}`
                                : `Meter ${u.fsp} a la VLAN ${v.id}`}
                            >
                              {puesto ? "✓" : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="olt-matriz__acciones">
                  <button
                    type="button"
                    className="sw-icono"
                    disabled={v.id === 1 || ocupado}
                    title={v.id === 1 ? "La VLAN 1 es la de fábrica" : `Borrar la VLAN ${v.id}`}
                    onClick={() => borrarVlan(v)}
                  >
                    <Iconos.eliminar size={16} strokeWidth={1.9} />
                  </button>
                </div>
              </div>
            );
          })}

          <div className="olt-matriz__renglon olt-matriz__renglon--nueva">
            <div className="olt-matriz__vlan">
              <input
                className="sw-campo olt-matriz__entrada"
                type="number"
                placeholder="VLAN"
                value={vlanNueva}
                onChange={(e) => setVlanNueva(e.target.value)}
                min={1}
                max={4094}
              />
              <em>nueva</em>
            </div>

            <div className="olt-matriz__puertos">
              {tarjetasDeSubida.map(([tarjeta, puertos]) => (
                <div key={tarjeta} className="olt-matriz__grupo">
                  <div className="olt-matriz__fila">
                    {puertos.map((u) => {
                      const puesto = uplinksNueva.includes(u.fsp);
                      return (
                        <button
                          key={u.fsp}
                          type="button"
                          className={`olt-celda${puesto ? " olt-celda--puesta" : ""}`}
                          onClick={() => setUplinksNueva((antes) => (
                            puesto ? antes.filter((x) => x !== u.fsp) : [...antes, u.fsp]
                          ))}
                          disabled={ocupado}
                          title={`${puesto ? "Quitar" : "Poner"} ${u.fsp} en la VLAN nueva`}
                        >
                          {puesto ? "✓" : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="olt-matriz__acciones">
              <Boton
                icono={Iconos.agregar}
                onClick={() => crearVlan(subida)}
                disabled={ocupado || !vlanNueva}
              >
                Crear
              </Boton>
            </div>
          </div>
        </div>

        <p className="sw-pie-leyenda">
          Una VLAN sin ningún puerto de subida no saca tráfico de la OLT.
          Pulsa una celda para meter o sacar ese puerto.
        </p>

        {vlans.length === 0 && <Crudo titulo="Salida de display vlan all" texto={crudo.vlan} />}
        {descuadre?.vlans?.length > 0 && (
          <Crudo
            titulo="Salida de display port vlan de cada puerto de subida"
            texto={Object.entries(crudo.puertos || {})
              .map(([fsp, txt]) => `# ${fsp}\n${txt}`)
              .join("\n")}
          />
        )}
      </Tarjeta>

      {/* ---------- gestión ---------- */}
      <Tarjeta titulo="IP de gestión">
        <div className="sw-equipo__datos sw-equipo__datos--tres">
          <div className="sw-dato">
            <span>MEth0</span>
            <strong>{meth.ip ? `${meth.ip}/${meth.prefijo}` : "—"}</strong>
          </div>
          <div className="sw-dato">
            <span>Interfaz</span>
            <strong>{[meth.estado, meth.enlace].filter(Boolean).join(" · ") || "—"}</strong>
          </div>
          <div className="sw-dato"><span>MAC</span><strong>{meth.mac || "—"}</strong></div>
        </div>

        {/* Que no coincidan significa que alguien la cambió por consola y
            la app sigue entrando por otra puerta. Se ve aquí o no se ve
            en ningún lado. */}
        {meth.ip && meth.ip !== host && (
          <div className="sw-aviso sw-aviso--suave">
            <Iconos.duda size={16} strokeWidth={2} />
            <div>
              <p><strong>El equipo tiene {meth.ip} y la app entra por {host}.</strong></p>
              <p>Funciona, pero conviene dejarlas iguales para no perseguir fantasmas después.</p>
            </div>
          </div>
        )}
        <div className="olt-gestion">
          <label className="olt-campo">
            <span>IP nueva</span>
            <input
              className="sw-campo"
              value={gestion.ip}
              onChange={(e) => setGestion((g) => ({ ...g, ip: e.target.value, tocada: true }))}
              placeholder={meth.ip || "10.7.8.20"}
            />
          </label>
          <label className="olt-campo">
            <span>Máscara</span>
            <input
              className="sw-campo"
              value={gestion.mascara}
              onChange={(e) => setGestion((g) => ({ ...g, mascara: e.target.value, tocada: true }))}
            />
          </label>
          <Boton
            variante="secundario"
            icono={Iconos.confirmar}
            onClick={cambiarIp}
            disabled={!gestion.ip || ocupado}
          >
            Cambiar
          </Boton>
        </div>
        {!meth.ip && <Crudo titulo="Salida de display interface meth 0" texto={crudo.meth} />}
      </Tarjeta>

      {/* ---------- lo que se va a mandar ---------- */}
      {plan && (
        <div className="sw-modal" role="dialog" onClick={() => !ocupado && setPlan(null)}>
          <div className="sw-modal__caja" onClick={(e) => e.stopPropagation()}>
            <div className="sw-modal__cabecera"><h3>{plan.titulo}</h3></div>
            <div className="sw-modal__cuerpo">
              <p>Se va a mandar a la OLT:</p>
              <pre className="sw-comandos">{plan.comandos.join("\n")}</pre>
              {plan.aviso && <p>{plan.aviso}</p>}
            </div>
            <div className="sw-modal__pie">
              <Boton variante="secundario" onClick={() => setPlan(null)} disabled={ocupado}>
                Cancelar
              </Boton>
              <Boton
                variante={plan.peligro ? "peligro" : "primario"}
                icono={Iconos.confirmar}
                onClick={aplicarPlan}
                cargando={ocupado}
              >
                Mandar
              </Boton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
